import * as React from 'react';
import { Component } from 'react';
import { AppUser } from '../../views/App/App';
import { GENERAL_MESSAGE_MODAL_TOTAL_STEP } from '../../consts/generalMessage';
import { uploadFileAllPath } from '../../helpers/service/file';
import {
  getGeneralMessageAttachmentContentHash,
  getGeneralMessageAttachmentUploadError,
  getGeneralMessageDuplicateAttachmentError,
  getGeneralMessageRequestError,
  isGeneralMessageAttachmentDuplicate,
  validateGeneralMessageAttachmentAddition,
  validateGeneralMessageAttachmentCollection
} from '../../helpers/generalMessageAttachments/generalMessageAttachmentSize';
import {
  createSchoolGeneralMessage,
  deleteSchoolGeneralMessage,
  getSchoolGeneralMessage,
  scheduleSchoolGeneralMessage,
  sendSchoolGeneralMessage,
  updateSchoolGeneralMessage
} from '../../helpers/service/admin/generalMessage';
import { Attachment, ParentsDeliverySettings } from '../../models/generalMessage';
import { getProgress } from '../../helpers/message/message';
import { TextUtils } from '../../helpers/utils/TextUtils';
import { GeneralMessageSettingsForm } from '../GeneralMessageSettingsForm/GeneralMessageSettingsForm';
import { GeneralMessageTextForm } from '../GeneralMessageTextForm/GeneralMessageTextForm';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { GeneralMessageModal } from '../GeneralMessageModal/GeneralMessageModal';
import { GeneralMessagePreviewWithTabs } from '../GeneralMessagePreview/GeneralMessagesPreviewWithTabs';
import { ACTION_TYPE } from '../GeneralMessageActionsButton/GeneralMessageActionsButton';
import { ScheduleSendingForm } from '../GeneralMessages/ScheduleSendingForm/ScheduleSendingForm';
import { HTML_FIRST_PART_PAGE_CODE, HTML_LAST_PART_PAGE_CODE } from '../HTMLEditorQuill/HTMLEditorConst';

interface Props {
  user: AppUser;
  onMessageSubmit: (message) => void;
  onCancelClick: () => void;
  students?: any[];
  schoolStaff?: any[];
  sendToStudents?: boolean;
  sendToParents?: boolean;
  sendToStaff?: boolean;
  isOpen: boolean;
  maxSizeUploadFile: number;
  fullScreen?: boolean;
}

interface StepState {
  currentStep: number;
}

interface ErrorState {
  isError: boolean;
  errorMessage: string;
}

interface ScheduleFormState {
  isScheduleFormVisible: boolean;
}

interface MessageState {
  messageId: string;

  //settings
  messageTitle: string;
  messageDescription: string;
  messageIsSendParents?: boolean;
  messageIsSendStudents?: boolean;
  messageIsSendStaff?: boolean;
  messageParentsDeliverySettings?: ParentsDeliverySettings;

  //content
  messageSmsText: string;
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;

  isSmsEditorEnabled: boolean;
  isPushEditorEnabled: boolean;
  isEmailEditorEnabled: boolean;
  isLoadingFile: boolean;

  //email attachments
  messageAttachments: Attachment[];
  isImageError: boolean;
  maxSizeUploadFile: number;

  messageScheduleAt: string;

  affectedUserList: any[];
}

type State = MessageState & StepState & ErrorState & ScheduleFormState;

export class GeneralMessageWizzard extends Component<Props, State> {
  private pendingAttachmentContentHashes = new Set<string>();
  private disabledChannelDrafts: {
    email?: { subject: string; body: string; attachments: Attachment[] };
    push?: { title: string; body: string };
    sms?: { text: string };
  } = {};

  constructor(props: Props) {
    super(props);

    this.state = {
      messageId: '',

      messageTitle: '',
      messageDescription: '',
      messageIsSendParents: props.sendToParents,
      messageIsSendStudents: props.sendToStudents,
      messageIsSendStaff: props.sendToStaff,

      messageParentsDeliverySettings: {
        all: true,
        firstPriority: false,
        secondPriority: false,
        thirdPriority: false
      },

      messageSmsText: '',
      messageEmailSubject: '',
      messageEmailBody: '',
      messagePushTitle: '',
      messagePushBody: '',

      isSmsEditorEnabled: true,
      isPushEditorEnabled: true,
      isEmailEditorEnabled: true,
      isLoadingFile: false,

      messageScheduleAt: '',

      affectedUserList: [],

      messageAttachments: [],

      currentStep: 1,

      isError: false,
      errorMessage: '',
      isImageError: false,
      maxSizeUploadFile: props.maxSizeUploadFile,

      isScheduleFormVisible: false
    };
  }

  // step callbacks
  onNextClick = (): void => {
    const {
      currentStep,
      isSmsEditorEnabled,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      messageEmailBody,
      messageEmailSubject,
      messagePushBody,
      messagePushTitle,
      messageSmsText
    } = this.state;

    let newMessageEmailBody;
    if (messageEmailBody === '<p><br></p>') {
      //  because   "<p><br></p>" === ''  in editor
      newMessageEmailBody = '';
      this.setState({
        messageEmailBody: newMessageEmailBody
      });
    } else {
      newMessageEmailBody = messageEmailBody;
    }

    const isCurrentStepContent = currentStep === 2;

    // returns an error if content is empty
    const isSMSTextExist = messageSmsText !== '';
    const isPushBodyExist = messagePushBody !== '';
    const isPushTitleExist = messagePushTitle !== '';
    const isEmailSubjectExist = messageEmailSubject !== '';
    const isEmailBodyExist = newMessageEmailBody !== '';

    const isShowSMSError = !isSMSTextExist && isSmsEditorEnabled;
    const isShowPushError = (!isPushBodyExist || !isPushTitleExist) && isPushEditorEnabled;
    const isShowEmailError = (!isEmailSubjectExist || !isEmailBodyExist) && isEmailEditorEnabled;

    if (isCurrentStepContent && (isShowSMSError || isShowPushError || isShowEmailError)) {
      let errorText = '';

      switch (true) {
        case isShowSMSError:
          errorText = 'Text for sms content should be defined';
          break;
        case isShowPushError:
          errorText = 'Title or body for push content should be defined';
          break;
        case isShowEmailError:
          errorText = 'Subject or body for email content should be defined';
          break;
      }
      this.setState({
        isError: true,
        errorMessage: errorText
      });
    } else {
      this.saveGeneralMessage().then(message => {
        this.setState(prevState => ({
          currentStep: prevState.currentStep + 1,
          messageId: message.id,
          affectedUserList: message.affectedUserList
        }));
      });
    }
  };

  onPreviousClick = (): void => {
    const { currentStep } = this.state;

    if (currentStep === 1) {
      return;
    }

    this.setState({
      currentStep: currentStep - 1
    });
  };

  // message settings callbacks
  onTitleChange = event => {
    this.setState({ messageTitle: event.target.value });
  };

  onDescriptionChange = event => {
    this.setState({ messageDescription: event.target.value });
  };

  onIsSendParentsChange = event => {
    const isSendParents = event.target.checked;

    this.setState({ messageIsSendParents: isSendParents });
  };

  onIsSendStudentsChange = event => {
    const isSendStudents = event.target.checked;

    this.setState({ messageIsSendStudents: isSendStudents });
  };

  onIsSendStaffChange = (event): void => {
    this.setState({
      messageIsSendStaff: event.target.checked
    });
  };

  onDeliverySettingsChanged = (settings: ParentsDeliverySettings): void => {
    this.setState({
      messageParentsDeliverySettings: settings
    });
  };

  // message content callbacks
  onEmailSubjectChange = event => {
    this.setState({ messageEmailSubject: event.target.value });
  };

  onEmailBodyChange = (body: any) => {
    this.setState({
      messageEmailBody: body
    });
  };

  onSmsTextChange = event => {
    this.setState({ messageSmsText: event.target.value });
  };

  onPushTitleChange = event => {
    this.setState({ messagePushTitle: event.target.value });
  };

  onPushBodyChange = event => {
    this.setState({ messagePushBody: event.target.value });
  };

  onEmailAttachmentChange = async event => {
    const { user } = this.props;
    const { maxSizeUploadFile, messageAttachments } = this.state;
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (!file) return;

    input.value = '';
    const currentFileSize = file.size;
    const fileName = file.name;
    const fileNameWithoutSpaces = fileName.split(' ').join('_');
    const validation = validateGeneralMessageAttachmentAddition(messageAttachments, currentFileSize, maxSizeUploadFile);

    if (!validation.isValid) {
      this.setState({
        errorMessage: validation.errorMessage || 'This file cannot be attached.',
        isError: true
      });
      return;
    }

    this.setState({ isLoadingFile: true });

    let contentHash: string;
    try {
      contentHash = await getGeneralMessageAttachmentContentHash(file);
    } catch (error) {
      this.setState({
        errorMessage: 'This attachment could not be read. Choose the file again and retry.',
        isError: true,
        isLoadingFile: false
      });
      return;
    }

    const candidateAttachment: Attachment = {
      fileName: fileNameWithoutSpaces,
      externalFileId: '',
      fileSize: currentFileSize,
      contentHash
    };
    if (
      isGeneralMessageAttachmentDuplicate(this.state.messageAttachments, candidateAttachment) ||
      this.pendingAttachmentContentHashes.has(contentHash)
    ) {
      this.setState({
        errorMessage: getGeneralMessageDuplicateAttachmentError(fileName),
        isError: true,
        isLoadingFile: false
      });
      return;
    }

    this.pendingAttachmentContentHashes.add(contentHash);

    const formData = new FormData();
    formData.append('file', file, fileNameWithoutSpaces);

    uploadFileAllPath(user, formData)
      .then(data => {
        const { key, link } = data;
        this.setState(state => ({
          messageAttachments: [
            ...state.messageAttachments,
            {
              fileName: fileNameWithoutSpaces,
              link: link,
              externalFileId: key,
              fileSize: currentFileSize,
              contentHash
            }
          ],
          isLoadingFile: false
        }));
        this.pendingAttachmentContentHashes.delete(contentHash);
      })
      .catch(error => {
        this.pendingAttachmentContentHashes.delete(contentHash);
        this.setState({
          errorMessage: getGeneralMessageAttachmentUploadError(error, maxSizeUploadFile),
          isError: true,
          isLoadingFile: false
        });
      });
  };

  onRemoveAttachment = (key: string) => {
    const attachments = [...this.state.messageAttachments];
    const attachmentIndex = attachments.findIndex(attachment => attachment.externalFileId === key);

    if (attachmentIndex < 0) return;

    attachments.splice(attachmentIndex, 1);

    this.setState({
      messageAttachments: attachments
    });
  };

  onIsEmailEditorChange = event => {
    const isEmailEditorEnabled = event.target.checked;

    if (!isEmailEditorEnabled && this.state.isEmailEditorEnabled) {
      this.disabledChannelDrafts.email = {
        subject: this.state.messageEmailSubject,
        body: this.state.messageEmailBody,
        attachments: [...this.state.messageAttachments]
      };
      this.setState({
        isEmailEditorEnabled: false,
        messageEmailSubject: '',
        messageEmailBody: '',
        messageAttachments: []
      });
      return;
    }

    if (isEmailEditorEnabled && !this.state.isEmailEditorEnabled) {
      const cached = this.disabledChannelDrafts.email;
      this.disabledChannelDrafts.email = undefined;
      if (cached) {
        this.setState({
          isEmailEditorEnabled: true,
          messageEmailSubject: cached.subject,
          messageEmailBody: cached.body,
          messageAttachments: [...cached.attachments]
        });
      } else {
        this.setState({ isEmailEditorEnabled: true });
      }
    }
  };

  onIsSmsEditorChange = event => {
    const isSmsEditorEnabled = event.target.checked;

    if (!isSmsEditorEnabled && this.state.isSmsEditorEnabled) {
      this.disabledChannelDrafts.sms = { text: this.state.messageSmsText };
      this.setState({ isSmsEditorEnabled: false, messageSmsText: '' });
      return;
    }

    if (isSmsEditorEnabled && !this.state.isSmsEditorEnabled) {
      const cached = this.disabledChannelDrafts.sms;
      this.disabledChannelDrafts.sms = undefined;
      if (cached) {
        this.setState({ isSmsEditorEnabled: true, messageSmsText: cached.text });
      } else {
        this.setState({ isSmsEditorEnabled: true });
      }
    }
  };

  onIsPushEditorChange = event => {
    const isPushEditorEnabled = event.target.checked;
    if (!isPushEditorEnabled && this.state.isPushEditorEnabled) {
      this.disabledChannelDrafts.push = {
        title: this.state.messagePushTitle,
        body: this.state.messagePushBody
      };
      this.setState({ isPushEditorEnabled: false, messagePushBody: '', messagePushTitle: '' });
      return;
    }
    if (isPushEditorEnabled && !this.state.isPushEditorEnabled) {
      const cached = this.disabledChannelDrafts.push;
      this.disabledChannelDrafts.push = undefined;
      if (cached) {
        this.setState({ isPushEditorEnabled: true, messagePushTitle: cached.title, messagePushBody: cached.body });
      } else {
        this.setState({ isPushEditorEnabled: true });
      }
    }
  };

  onCloseImageError = () => {
    this.setState({
      isImageError: false
    });
  };

  // error callbacks
  onShowError = (message: string) => {
    const errorMessage = typeof message === 'string' ? message : 'Error saving general message';

    this.setState({
      isError: true,
      errorMessage: errorMessage
    });
  };

  onCloseErrorClick = event => {
    event.preventDefault();

    this.setState({
      isError: false,
      errorMessage: ''
    });
  };

  // create message callbacks
  onSaveMessageClick = () => {
    const { onMessageSubmit } = this.props;

    this.saveGeneralMessage()
      .then(response => {
        onMessageSubmit(response);
      })
      .catch(reason => {
        console.error(reason);
        this.onShowError(getGeneralMessageRequestError(reason, 'Error saving general message'));
      });
  };

  onSendMessageModalClick = () => {
    const { user, onMessageSubmit } = this.props;

    this.saveGeneralMessage()
      .then(response => sendSchoolGeneralMessage(user, response.id))
      .then(response => {
        onMessageSubmit(response);
      })
      .catch(error => {
        console.error(error);
        this.onShowError(getGeneralMessageRequestError(error, 'Error sending general message'));
      });
  };

  onScheduleMessageClick = () => {
    if (!this.isScheduleDateValid()) {
      return;
    }

    const { user, onMessageSubmit } = this.props;
    const { messageScheduleAt } = this.state;
    let newMessage;

    this.saveGeneralMessage()
      .then(message => {
        newMessage = message;
        return scheduleSchoolGeneralMessage(user, message.id, messageScheduleAt);
      })
      .then(() => {
        // there is message with status DRAFT and we need to reload message from server with new status
        return getSchoolGeneralMessage(user, newMessage.id);
      })
      .then(message => {
        onMessageSubmit(message);
      })
      .catch(reason => {
        console.error(reason);
        this.onShowError(getGeneralMessageRequestError(reason, 'Error scheduling general message'));
      });
  };

  onActiveActionChange = (actionType: ACTION_TYPE) => {
    this.setState({
      isScheduleFormVisible: actionType === ACTION_TYPE.SCHEDULE
    });
  };

  onScheduleDateChange = (event: any) => {
    this.setState({
      messageScheduleAt: event.target.value || ''
    });
  };

  onCancelClick = () => {
    const { user } = this.props;
    const { messageId } = this.state;

    if (TextUtils.isEmpty(messageId)) {
      this.props.onCancelClick();
      return;
    }

    deleteSchoolGeneralMessage(user, messageId).then(() => this.props.onCancelClick());
  };

  saveGeneralMessage = () => {
    const {
      messageId,
      messageTitle,
      messageDescription,
      messageSmsText,
      messageEmailSubject,
      messageEmailBody,
      messageAttachments,
      messagePushTitle,
      messagePushBody,
      messageIsSendStaff,
      messageIsSendParents,
      messageIsSendStudents,
      messageParentsDeliverySettings
    } = this.state;

    const attachmentValidation = validateGeneralMessageAttachmentCollection(
      messageAttachments,
      this.state.maxSizeUploadFile
    );

    if (!attachmentValidation.isValid) {
      return Promise.reject(new Error(attachmentValidation.errorMessage || 'The attachments could not be validated.'));
    }

    const { user, schoolStaff, students } = this.props;

    const staffs =
      typeof schoolStaff !== 'undefined'
        ? schoolStaff.map(staff => ({ userId: staff.id, permissionId: staff.permissions[0].id }))
        : undefined;
    const studentsList =
      typeof students !== 'undefined'
        ? students.map(student => ({ userId: student.id, permissionId: student.permissionId }))
        : undefined;
    const messageHTMLEmailBody =
      messageEmailBody !== '' ? `${HTML_FIRST_PART_PAGE_CODE} ${messageEmailBody} ${HTML_LAST_PART_PAGE_CODE}` : '';

    const data = {
      title: messageTitle,
      description: messageDescription,
      content: {
        sms: { text: messageSmsText },
        email: { subject: messageEmailSubject, body: messageHTMLEmailBody, attachments: messageAttachments },
        push: { title: messagePushTitle, body: messagePushBody }
      },
      subscribers: {
        types: {
          schoolStaff: messageIsSendStaff,
          parents: messageIsSendParents,
          students: messageIsSendStudents
        },
        lists: {
          staffs: staffs,
          students: studentsList
        },
        parentsDeliverySettings: messageParentsDeliverySettings
      }
    };

    if (!TextUtils.isEmpty(messageId)) {
      return updateSchoolGeneralMessage(user, data, messageId);
    } else {
      return createSchoolGeneralMessage(user, data);
    }
  };

  isNextButtonDisabled(): boolean {
    const {
      currentStep,
      messageIsSendStaff,
      messageIsSendStudents,
      messageIsSendParents,
      affectedUserList,
      isSmsEditorEnabled,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      isLoadingFile,
      isError
    } = this.state;

    let isNextButtonDisabled = false;

    const isAllEditorDisabled = !isSmsEditorEnabled && !isPushEditorEnabled && !isEmailEditorEnabled;

    switch (currentStep) {
      case 1:
        isNextButtonDisabled = !messageIsSendStaff && !messageIsSendParents && !messageIsSendStudents;
        break;
      case 2:
        isNextButtonDisabled = isError || isLoadingFile || isAllEditorDisabled;
        break;
      case 3:
        isNextButtonDisabled = typeof affectedUserList === 'undefined' || affectedUserList.length === 0;
        break;
    }

    return isNextButtonDisabled;
  }

  isScheduleDateValid = (): boolean => {
    const { messageScheduleAt } = this.state;

    if (TextUtils.isEmpty(messageScheduleAt)) {
      this.onShowError('Please enter a valid date');
      return false;
    }

    const scheduleDate = new Date(messageScheduleAt);
    const scheduleTime = scheduleDate.getTime();

    if (Number.isNaN(scheduleTime)) {
      this.onShowError('Please enter a valid date');
      return false;
    }

    if (Date.now() > scheduleTime) {
      this.onShowError('The scheduled sending time is in the past, please amend it');
      return false;
    }

    return true;
  };

  renderViewByStep(): React.ReactNode {
    const {
      currentStep,
      messageSmsText,
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageTitle,
      messageDescription,
      messageAttachments,
      messageParentsDeliverySettings,
      messageIsSendParents,
      messageIsSendStudents,
      messageIsSendStaff,
      affectedUserList,
      isImageError,
      isError,
      isSmsEditorEnabled,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      isLoadingFile
    } = this.state;

    switch (currentStep) {
      case 1:
        return (
          <GeneralMessageSettingsForm
            messageTitle={messageTitle}
            messageDescription={messageDescription}
            parentsDeliverySettings={messageParentsDeliverySettings}
            isSendParents={messageIsSendParents}
            isSendStudents={messageIsSendStudents}
            isSendStaff={messageIsSendStaff}
            onTitleChange={this.onTitleChange}
            onDescriptionChange={this.onDescriptionChange}
            onIsSendParentsChange={this.onIsSendParentsChange}
            onIsSendStudentsChange={this.onIsSendStudentsChange}
            onSendStaffChange={this.onIsSendStaffChange}
            onDeliverySettingsChanged={this.onDeliverySettingsChanged}
          />
        );
      case 2:
        return (
          <GeneralMessageTextForm
            user={this.props.user}
            onEmailSubjectChange={this.onEmailSubjectChange}
            onEmailBodyChange={this.onEmailBodyChange}
            onSmsTextChange={this.onSmsTextChange}
            onPushTitleChange={this.onPushTitleChange}
            onPushBodyChange={this.onPushBodyChange}
            onEmailAttachmentChange={this.onEmailAttachmentChange}
            onRemoveAttachment={this.onRemoveAttachment}
            onCloseImageError={this.onCloseImageError}
            messageSmsText={messageSmsText}
            messageEmailSubject={messageEmailSubject}
            messageEmailBody={messageEmailBody}
            messagePushTitle={messagePushTitle}
            messagePushBody={messagePushBody}
            messageAttachments={messageAttachments}
            isImageError={isImageError}
            isError={isError}
            isLoadingFile={isLoadingFile}
            isEmailEditorEnabled={isEmailEditorEnabled}
            isSmsEditorEnabled={isSmsEditorEnabled}
            isPushEditorEnabled={isPushEditorEnabled}
            onIsEmailEditorChange={this.onIsEmailEditorChange}
            onIsSmsEditorChange={this.onIsSmsEditorChange}
            onIsPushEditorChange={this.onIsPushEditorChange}
          />
        );
      case 3:
        return (
          <GeneralMessagePreviewWithTabs
            messageSmsText={messageSmsText}
            messageEmailSubject={messageEmailSubject}
            messageEmailBody={messageEmailBody}
            messagePushTitle={messagePushTitle}
            messagePushBody={messagePushBody}
            messageAttachments={messageAttachments}
            affectedUsers={affectedUserList}
            isEmailEnabled={isEmailEditorEnabled}
            isPushEnabled={isPushEditorEnabled}
            isSmsEnabled={isSmsEditorEnabled}
          />
        );
    }
  }

  renderErrorAlert(): React.ReactNode {
    const { errorMessage, isError } = this.state;

    if (!isError) {
      return null;
    }

    return (
      <div className="alert fade show alert-danger d-flex justify-content-between align-items-center" role="alert">
        {errorMessage}
        <button
          type="button"
          className="ml-3 btn btn-danger"
          aria-label="Close"
          onClick={event => this.onCloseErrorClick(event)}
        >
          Close
        </button>
      </div>
    );
  }

  render() {
    const { isOpen, fullScreen } = this.props;
    const { currentStep, isScheduleFormVisible, isError } = this.state;

    return (
      <GeneralMessageModal
        isOpen={isOpen}
        title={'New message'}
        closeButtonText={'Close'}
        submitButtonText={'Next step'}
        onCloseClick={this.onCancelClick}
        onPreviousClick={this.onPreviousClick}
        onNextClick={this.onNextClick}
        onSaveClick={this.onSaveMessageClick}
        onSendClick={this.onSendMessageModalClick}
        onScheduleClick={this.onScheduleMessageClick}
        hasNext={currentStep !== GENERAL_MESSAGE_MODAL_TOTAL_STEP}
        hasPrevious={currentStep > 1}
        isNextButtonDisabled={this.isNextButtonDisabled()}
        onActiveActionChange={this.onActiveActionChange}
        isError={isError}
        fullScreen={fullScreen}
      >
        {this.renderErrorAlert()}

        {isScheduleFormVisible && <ScheduleSendingForm onScheduleDateChange={this.onScheduleDateChange} />}

        <ProgressBar
          progress={getProgress(currentStep, GENERAL_MESSAGE_MODAL_TOTAL_STEP)}
          step={currentStep}
          stepTotal={GENERAL_MESSAGE_MODAL_TOTAL_STEP}
        />
        {this.renderViewByStep()}
      </GeneralMessageModal>
    );
  }
}
